/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/qaTemplateInsert.ts
 * FILE_ID: QA-TEMPLATES-INSERT-001
 * PURPOSE: Insert one basket of templates into an inspection and write products.
 */
import type { QaApi } from '../../../qa/qaApi'
import type {
  QaTemplateDetail,
  QaTemplateInsertResult,
} from '../../../qa/qaTemplateTypes'
import { buildInsertPayload } from './qaTemplateCatalog'
import type { QaTemplateSelection } from './useQaTemplatePicker'
import {
  insertedNamesNeedPlacements,
  productWritesForInsert,
  type QaTemplateProductDraft,
} from './qaTemplateProductDraft'

export type QaTemplateBasketRow = {
  id: string
  detail: QaTemplateDetail
  selection: QaTemplateSelection
}

export function emptyMergedResult(): QaTemplateInsertResult {
  return {
    created: [],
    counts: { areas: 0, checklists: 0, items: 0 },
    location_area_id: undefined,
    placements: [],
  }
}

export function mergeInsertResults(
  into: QaTemplateInsertResult,
  next: QaTemplateInsertResult,
): QaTemplateInsertResult {
  return {
    created: [...into.created, ...next.created],
    counts: {
      areas: (into.counts?.areas ?? 0) + (next.counts?.areas ?? 0),
      checklists: (into.counts?.checklists ?? 0) + (next.counts?.checklists ?? 0),
      items: (into.counts?.items ?? 0) + (next.counts?.items ?? 0),
    },
    location_area_id: next.location_area_id || into.location_area_id,
    placements: [...(into.placements ?? []), ...(next.placements ?? [])],
  }
}

export class TemplateInsertError extends Error {
  done: number
  merged: QaTemplateInsertResult
  constructor(message: string, done: number, merged: QaTemplateInsertResult) {
    super(message)
    this.name = 'TemplateInsertError'
    this.done = done
    this.merged = merged
  }
}

export async function runTemplateInserts(args: {
  api: QaApi
  rows: QaTemplateBasketRow[]
  targetAreaIds: string[]
  inspectionId: string
  productDraft: QaTemplateProductDraft
  intoChecklistId?: string
}): Promise<{ merged: QaTemplateInsertResult; done: number }> {
  let done = 0
  let merged = emptyMergedResult()
  for (const row of args.rows) {
    try {
    const payload = buildInsertPayload(
      row.detail,
      args.targetAreaIds,
      row.selection.itemIds,
      row.selection.sectionTitles,
      row.selection.itemTitles,
      args.inspectionId,
    )
    if (args.intoChecklistId) payload.checklist_id = args.intoChecklistId
    const inserted = await args.api.insertTemplate(row.id, payload)
    const rowLocation = row.detail.location_type || null
    if (!inserted.placements && insertedNamesNeedPlacements(args.productDraft, rowLocation, row.selection.itemIds)) {
      throw new Error('Could not save the products.')
    }
    if (inserted.placements) {
      const writes = productWritesForInsert(inserted.placements, args.productDraft, rowLocation)
      const checklistWrites = args.intoChecklistId ? [] : writes.checklists
      await Promise.all([
        ...checklistWrites.map((entry) => args.api.setChecklistProductNames(entry.id, entry.names)),
        ...writes.items.map((entry) => args.api.setItemContent(entry.id, { product_names: entry.names })),
      ])
    }
    merged = mergeInsertResults(merged, inserted)
    done += 1
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not insert the template.'
      throw new TemplateInsertError(message, done, merged)
    }
  }
  return { merged, done }
}
